import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import LookupSelect from '../components/LookupSelect';
import { useModal } from '../components/useModal';
import { lookupFacility } from '../lookups';
import { LANGUAGE_OPTIONS } from '../../components/pages/seoOptions';

/**
 * Facility text — the copy of one facility in one language: the text shown on the page, the
 * SEO title, the meta description and the H1.
 *
 * Sibling of the SEO text screen, which does the same job for the area/sport landing pages.
 * The two keys here are the facility and the language: changing one of them on an existing
 * entry would not edit that page, it would move the texts to a different one, so on an entry
 * that already exists they are shown and not edited.
 *
 * An empty field is not an empty page: it means the site keeps generating that part. Clearing
 * all four removes the entry, which is how an editor goes back to the generated texts.
 */

const TITLE_MAX = 60;
const META_MIN = 120;
const META_MAX = 155;
const H1_MAX = 200;

const EMPTY = { MetaTitle: '', MetaDescription: '', H1Title: '', Description: '' };

const FacilityTextInternal = ({ api }) => {
  const { facilityTextId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ask, modalProps } = useModal();

  const isNew = !facilityTextId;

  const [row, setRow] = useState(isNew ? {} : null);
  const [form, setForm] = useState(isNew ? EMPTY : null);
  const [facility, setFacility] = useState(null);
  const [language, setLanguage] = useState(LANGUAGE_OPTIONS[0].value);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const toForm = (data) => ({
    MetaTitle: data.metaTitle || '',
    MetaDescription: data.metaDescription || '',
    H1Title: data.h1Title || '',
    Description: data.description || ''
  });

  const load = useCallback(async () => {
    if (isNew) {
      // Arriving from a facility screen: the facility is already decided.
      const preset = searchParams.get('facilityId');
      if (preset) setFacility(preset);
      return;
    }
    try {
      const data = await api.facilityContent.getFacilityText(facilityTextId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setRow(data);
      setForm(toForm(data));
      setFacility(data.facilityId);
      setLanguage(String(data.language));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, facilityTextId, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/facility-texts">← All facility texts</BackToList>
        <div className="next-note next-note--error">
          Could not load this facility text.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!form) return <div className="next-page next-muted">Loading…</div>;

  const set = (name, value) => setForm((previous) => ({ ...previous, [name]: value }));

  const pristine = isNew ? EMPTY : toForm(row);
  const dirty = Object.keys(form).some((key) => form[key] !== pristine[key]);

  const titleLength = form.MetaTitle.trim().length;
  const metaLength = form.MetaDescription.trim().length;
  const h1Length = form.H1Title.trim().length;

  const errors = {
    MetaTitle: titleLength > TITLE_MAX ? `${titleLength} characters — the maximum is ${TITLE_MAX}.` : null,
    MetaDescription: metaLength > 0 && (metaLength < META_MIN || metaLength > META_MAX)
      ? `${metaLength} characters — it must be between ${META_MIN} and ${META_MAX}.`
      : null,
    H1Title: h1Length > H1_MAX ? `${h1Length} characters — the maximum is ${H1_MAX}.` : null
  };
  const invalid = !facility || Object.values(errors).some(Boolean);

  const save = async (event) => {
    event.preventDefault();
    if (invalid || (!dirty && !isNew) || busy) return;

    setBusy(true);
    setNote(null);
    try {
      const result = await api.facilityContent.saveFacilityText({
        FacilityId: facility,
        Language: parseInt(language, 10),
        MetaTitle: form.MetaTitle.trim() || null,
        MetaDescription: form.MetaDescription.trim() || null,
        H1Title: form.H1Title.trim() || null,
        Description: form.Description.trim() || null
      });

      if (result?.success === false) {
        setNote({ tone: 'error', text: result.message || 'The texts were not saved.' });
        return;
      }

      if (result?.content == null) {
        navigate('/next/facility-texts');
        return;
      }

      if (isNew) {
        navigate(`/next/facility-text/${result.content.id}`);
        return;
      }

      await load();
      setNote({ tone: 'info', text: 'Saved. The site picks it up within a couple of minutes.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.payload?.message || error?.response?.data?.message
          || error?.message || 'Unable to save the texts.'
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => ask({
    kind: 'danger',
    title: 'Delete these texts?',
    context: `Delete · ${row.facilityName || ''}`,
    body: 'The facility page goes back to the generated title, meta and heading, and the text block disappears. The site picks the change up within a couple of minutes.',
    warning: 'The texts are not recoverable from the admin.',
    confirmLabel: 'Delete texts',
    onConfirm: async () => {
      try {
        await api.facilityContent.deleteFacilityText({ facilityId: facility, contentLanguage: parseInt(language, 10) });
      } catch (error) {
        setNote({ tone: 'error', text: error?.message || 'Unable to delete them.' });
        throw error;
      }
      navigate('/next/facility-texts');
    }
  });

  const field = (name, label, { rows, hint, counter, bad } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      {rows
        ? (
          <textarea
            className="next-input next-textarea"
            rows={rows}
            value={form[name]}
            disabled={busy}
            onChange={(event) => set(name, event.target.value)}
          />
        )
        : (
          <input
            className="next-input"
            value={form[name]}
            disabled={busy}
            onChange={(event) => set(name, event.target.value)}
          />
        )}
      {counter && <span className={`next-form__hint${bad ? ' next-form__error' : ''}`}>{counter}</span>}
      {hint && <span className="next-form__hint">{hint}</span>}
      {errors[name] && <span className="next-form__error">{errors[name]}</span>}
    </label>
  );

  const languageName = (LANGUAGE_OPTIONS.find((option) => option.value === String(language)) || {}).label || language;

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/facility-texts">← All facility texts</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{isNew ? 'New facility text' : (row.facilityName || 'Facility text')}</h1>
          <span className="next-pill next-pill--info">{languageName}</span>
        </div>
        {!isNew && <span className="next-detail__id">#{row.id}</span>}
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head">
                <h2>Which page</h2>
                <span className="next-muted">the facility and the language identify the entry</span>
              </div>
              <div className="next-form__grid">
                {isNew ? (
                  <LookupSelect
                    label="Facility"
                    value={facility}
                    onChange={setFacility}
                    loadOptions={(key) => lookupFacility(api, key)}
                    placeholder="Name or id"
                  />
                ) : (
                  <label className="next-filter">
                    <span className="next-filter__label">Facility</span>
                    <span className="next-fact__value">{row.facilityName || row.facilityId}</span>
                  </label>
                )}

                <label className="next-filter">
                  <span className="next-filter__label">Language</span>
                  <select
                    className="next-input"
                    value={language}
                    disabled={!isNew || busy}
                    onChange={(event) => setLanguage(event.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <span className="next-form__hint">
                    Texts are never shown under another language: what is empty here stays generated
                    on the {languageName} page.
                  </span>
                </label>
              </div>
            </section>

            <section className="next-card">
              <div className="next-card__head">
                <h2>On the page</h2>
                <span className="next-muted">anything left empty keeps the generated value</span>
              </div>
              <div className="next-form__grid">
                {field('MetaTitle', 'SEO title', {
                  counter: `${titleLength}/${TITLE_MAX}`,
                  bad: !!errors.MetaTitle,
                  hint: 'The <title> in the search result. Not the facility name: lists and breadcrumbs keep showing the name.'
                })}
                {field('H1Title', 'H1 heading', {
                  counter: `${h1Length} characters`,
                  bad: !!errors.H1Title,
                  hint: 'The heading at the top of the page; not the title tag, and no SEO length limit. Keep the facility name in it — the name is what people search for. Add to it rather than replace it.'
                })}
              </div>
              <div className="next-form__row">
                {field('MetaDescription', 'Meta description', {
                  rows: 3,
                  counter: `${metaLength} characters (${META_MIN}–${META_MAX})`,
                  bad: !!errors.MetaDescription,
                  hint: 'The grey text under the title in Google.'
                })}
              </div>
              <div className="next-form__row">
                {field('Description', 'Facility text', {
                  rows: 10,
                  hint: 'Markdown. Shown in the block near the bottom of the facility page.'
                })}
              </div>

              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || (!dirty && !isNew) || busy}
                  title={!facility ? 'A facility is required' : undefined}
                >
                  {busy ? 'Saving…' : (isNew ? 'Create texts' : 'Save changes')}
                </button>
                {!isNew && (
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!dirty || busy}
                    onClick={() => setForm(toForm(row))}
                  >
                    Discard
                  </button>
                )}
                <span className="next-muted">The site picks a save up within a couple of minutes.</span>
              </div>
            </section>
          </form>
        </div>

        <aside className="next-detail__side">
          {!isNew && (
            <section className="next-card">
              <div className="next-card__head"><h2>Actions</h2></div>
              <div className="next-actionlist">
                <button
                  type="button"
                  className="next-actionlist__item next-actionlist__item--danger"
                  onClick={remove}
                >
                  Delete texts
                </button>
              </div>
            </section>
          )}

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                facility && {
                  label: 'Other languages for this facility',
                  hint: row.facilityName || undefined,
                  to: `/next/facility-texts?facilityId=${encodeURIComponent(facility)}`
                },
                facility && {
                  label: 'The facility',
                  to: `/next/facility/${facility}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

FacilityTextInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const FacilityText = withApi(FacilityTextInternal);

FacilityText.requiresLogin = true;

export default FacilityText;
